"use client" 

import { useState, useEffect, useRef, useMemo } from "react";
import { Send, Square } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";

import { useRTL } from '../../contexts/RTLContext';
import { useFloating } from '../../contexts/FloatingContext';
import { getPlaceholderText, getInputDirection } from '../../utils/rtl';
import { getParentLogoUrl, getParentDomain } from '../../services/logo.service';
import aiStarsIcon from '../../assets/ai-stars.png';
import { estimateTokenCount } from '../../utils/tokenUtils';

const TOKEN_LIMIT = 100;
 
const PLACEHOLDERS_EN = [
  "Ask me about this article",
  "What are the key points?",
  "Summarize this content",
  "What questions can I answer?",
  "How can I help you today?",
  "What would you like to know?",
];

const PLACEHOLDERS_HE = [
  "שאל אותי על המאמר הזה",
  "מהן הנקודות המרכזיות?",
  "סכם את התוכן",
  "על איזה שאלות אני יכול לענות?",
  "איך אני יכול לעזור לך היום?",
  "מה היית רוצה לדעת?",
];

const PLACEHOLDERS_AR = [
  "اسألني عن هذا المقال",
  "ما هي النقاط الرئيسية؟",
  "لخص هذا المحتوى",
  "ما الأسئلة التي يمكنني الإجابة عليها؟",
  "كيف يمكنني مساعدتك اليوم؟",
  "ماذا تريد أن تعرف؟",
];
 
interface AIChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  isStreaming?: boolean;
  onStop?: () => void;
  placeholder?: string[];
  onFocusChange?: (focused: boolean) => void;
  projectIconUrl?: string | null;
  // Analytics callbacks
  onInputFocused?: () => void;
  onInputBlurred?: () => void;
  onUserTyping?: (inputLength: number) => void;
}

const AIChatInput = ({ 
  onSend, 
  disabled = false,
  isStreaming = false,
  onStop,
  placeholder,
  onFocusChange,
  projectIconUrl,
  onInputFocused,
  onInputBlurred,
  onUserTyping
}: AIChatInputProps) => {
  const { isRTL, language } = useRTL();
  const { isFloating } = useFloating();
  


  // Get language-specific placeholders
  const getPlaceholders = () => {
    if (placeholder) return placeholder;
    
    switch (language) {
      case 'heb':
        return PLACEHOLDERS_HE;
      case 'arb':
        return PLACEHOLDERS_AR;
      default:
        return PLACEHOLDERS_EN;
    }
  };
  
  const placeholders = getPlaceholders();
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const [inputValue, setInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [textareaHeight, setTextareaHeight] = useState(44); // Base height for 1 line
  const wrapperRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Track typing session to avoid sending analytics on every character
  const typingSessionRef = useRef<{
    hasStarted: boolean;
    timeoutId: number | null;
  }>({ hasStarted: false, timeoutId: null });

  // Handle typing analytics - only track when user starts typing
  const handleTypingAnalytics = (inputLength: number) => {
    // Only send analytics event when user starts typing (not on every character)
    if (!typingSessionRef.current.hasStarted && inputLength > 0) {
      onUserTyping?.(inputLength);
      typingSessionRef.current.hasStarted = true;
    }
    
    // Reset typing session after user stops typing for 2 seconds
    if (typingSessionRef.current.timeoutId) {
      clearTimeout(typingSessionRef.current.timeoutId);
    }
    
    typingSessionRef.current.timeoutId = setTimeout(() => {
      typingSessionRef.current.hasStarted = false;
      typingSessionRef.current.timeoutId = null;
    }, 2000) as unknown as number;
  };

  // Memoized token count calculation
  const tokenCount = useMemo(() => estimateTokenCount(inputValue), [inputValue]);
  const isOverLimit = tokenCount > TOKEN_LIMIT;
  const isNearLimit = tokenCount > TOKEN_LIMIT * 0.8; // 80% threshold for warning


  // Calculate textarea height based on content and cursor position
  const calculateHeight = (value: string) => {
    if (!textareaRef.current) return 44;
    
    // Count actual lines including empty ones
    const lines = value.split('\n');
    const lineCount = Math.max(1, lines.length);
    
    // Calculate height based on line count
    const lineHeight = 24; // 1.5 * 16px
    const maxLines = 5;
    const constrainedLines = Math.min(lineCount, maxLines);
    
    return Math.max(44, constrainedLines * lineHeight + 16); // 16px for padding
  };

  // Update height when input value changes
  useEffect(() => {
    const newHeight = calculateHeight(inputValue);
    setTextareaHeight(newHeight);
  }, [inputValue]);

  // Reset typing session when input is cleared
  useEffect(() => {
    if (inputValue === '') {
      if (typingSessionRef.current.timeoutId) {
        clearTimeout(typingSessionRef.current.timeoutId);
      }
      typingSessionRef.current.hasStarted = false;
      typingSessionRef.current.timeoutId = null;
    }
  }, [inputValue]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (typingSessionRef.current.timeoutId) {
        clearTimeout(typingSessionRef.current.timeoutId);
      }
    };
  }, []);
 
  // Cycle placeholder text when input is inactive
  useEffect(() => {
    if (inputValue) return;
 
    const interval = setInterval(() => {
      setShowPlaceholder(false);
      setTimeout(() => {
        setPlaceholderIndex((prev) => (prev + 1) % placeholders.length);
        setShowPlaceholder(true);
      }, 400);
    }, 3000);
 
    return () => clearInterval(interval);
  }, [inputValue]);

  // Calculate dynamic container height - account for token counter
  const getContainerHeight = () => {
    const baseHeight = 56; // Reduced base height for more compact look
    const additionalTextHeight = Math.max(0, textareaHeight - 44); // Extra height beyond single line
    const errorMessageHeight = isOverLimit ? 20 : 0; // Space for error message when over limit
    
    return baseHeight + additionalTextHeight + errorMessageHeight;
  };

  // Determine if we have multiline content
  const isMultiline = inputValue.includes('\n') || textareaHeight > 44;
 
  const containerVariants = {
    animate: {
      height: getContainerHeight(),
      boxShadow: isFocused 
        ? "0 10px 40px -10px rgba(59, 130, 246, 0.25), 0 10px 25px -5px rgba(0, 0, 0, 0.1)"
        : "0 4px 20px -4px rgba(0, 0, 0, 0.1), 0 2px 10px -2px rgba(59, 130, 246, 0.1)",
      transition: { type: "spring" as const, stiffness: 120, damping: 18 },
    },
  };
 
  const placeholderContainerVariants = {
    initial: {},
    animate: { transition: { staggerChildren: 0.025 } },
    exit: { transition: { staggerChildren: 0.015, staggerDirection: -1 } },
  };
 
  const letterVariants = {
    initial: {
      opacity: 0,
      filter: "blur(12px)",
      y: 10,
    },
    animate: {
      opacity: 1,
      filter: "blur(0px)",
      y: 0,
      transition: {
        opacity: { duration: 0.25 },
        filter: { duration: 0.4 },
        y: { type: "spring" as const, stiffness: 80, damping: 20 },
      },
    },
    exit: {
      opacity: 0,
      filter: "blur(12px)",
      y: -10,
      transition: {
        opacity: { duration: 0.2 },
        filter: { duration: 0.3 },
        y: { type: "spring" as const, stiffness: 80, damping: 20 },
      },
    },
  };

  const handleSend = () => {
    if (inputValue.trim() && !disabled && !isOverLimit) {
      onSend(inputValue.trim());
      setInputValue("");
    }
  };

  // Get token counter color based on usage
  const getTokenCounterColor = () => {
    if (isOverLimit) return 'text-red-500';
    if (isNearLimit) return 'text-orange-500';
    return 'text-gray-500';
  };
 
  // Add a state to track animation progress
  const [gradientPosition, setGradientPosition] = useState(0);
  
  // Animate the gradient position
  useEffect(() => {
    const interval = setInterval(() => {
      setGradientPosition((prev) => (prev + 1) % 200);
    }, 30);
    return () => clearInterval(interval);
  }, []);

  return (
    <div 
      className="w-full flex justify-center items-center text-black px-2 sm:px-0"
    >
      <motion.div
        ref={wrapperRef}
        className="w-full max-w-3xl relative"
        variants={containerVariants}
        animate="animate"
        style={{ 
          padding: "1.5px",
          borderRadius: 33.5,
          background: `linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899, #8b5cf6, #3b82f6)`,
          backgroundSize: "200% 100%",
          backgroundPosition: `${gradientPosition}% 50%`,
          transition: "background-position 0.03s linear"
        }}
      >
        <div 
          className="w-full h-full"
          style={{
            borderRadius: 32,
            background: isFloating ? "rgba(255, 255, 255, 0.95)" : "#ffffff",
            overflow: "hidden",
          }}
        >
        <div className="flex flex-col items-stretch w-full h-full justify-center">
          {/* Input Row */}
          <div className={`flex items-center gap-1 px-4 py-2 rounded-full bg-white max-w-3xl w-full`}>
            
            {/* Text Input & Placeholder */}
            <div className="relative flex-1 min-h-[44px] flex items-center">
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  handleTypingAnalytics(e.target.value.length);
                }}
                onFocus={() => {
                  setIsFocused(true)
                  onFocusChange?.(true)
                  onInputFocused?.()
                }}
                onBlur={() => {
                  setIsFocused(false)
                  onFocusChange?.(false)
                  onInputBlurred?.()
                }}
                className={`flex-1 border-0 outline-none rounded-md py-2 text-base bg-transparent w-full font-normal resize-none overflow-y-auto placeholder-gray-500 focus:outline-none focus:ring-0 focus:border-transparent ${
                  isOverLimit ? 'text-red-600' : 'text-gray-900'
                }`}
                style={{ 
                  position: "relative", 
                  zIndex: 1,
                  height: `${textareaHeight}px`,
                  maxHeight: `${5 * 24 + 16}px`, // 5 lines max + padding
                  lineHeight: '1.5',
                  textAlign: isRTL ? 'right' : 'left',
                  direction: isRTL ? 'rtl' : 'ltr',
                  // Mobile focus fix
                  outline: 'none',
                  border: 'none',
                  WebkitAppearance: 'none',
                  WebkitTapHighlightColor: 'transparent',
                  boxShadow: 'none'
                }}
                dir={isRTL ? 'rtl' : 'ltr'}
                disabled={disabled}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (e.shiftKey) {
                      // Shift+Enter: allow new line
                      return;
                    } else {
                      // Enter: send message
                      if (inputValue.trim() && !disabled && !isOverLimit) {
                        e.preventDefault();
                        handleSend();
                      }
                    }
                  }
                }}
              />
              <div className="absolute left-0 top-0 w-full h-full pointer-events-none flex items-center px-3 py-2">
                <div className={`flex items-center gap-2 w-full ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
                  {/* Logo and AI section - hide when focused or has input */}
                  {!isFocused && !inputValue && (
                    <div className={`flex items-center gap-2 shrink-0 ${isRTL ? 'order-2' : 'order-1'}`}>
                      {/* AI Stars Icon - first in RTL, second in LTR */}
                      <motion.div 
                        className={`relative ${isRTL ? 'order-1' : 'order-2'}`}
                        animate={{
                          scale: [1, 1.1, 1],
                          rotate: [0, 5, -5, 0],
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                      >
                        <motion.div 
                          className="w-5 h-5"
                          style={{
                            mask: `url(${aiStarsIcon}) no-repeat center/contain`,
                            WebkitMask: `url(${aiStarsIcon}) no-repeat center/contain`,
                            background: `linear-gradient(135deg, #3b82f6, #8b5cf6)`,
                            filter: `drop-shadow(0 0 3px rgba(139, 92, 246, 0.5))`,
                          }}
                          animate={{
                            filter: [
                              "drop-shadow(0 0 3px rgba(139, 92, 246, 0.3))",
                              "drop-shadow(0 0 6px rgba(139, 92, 246, 0.6))",
                              "drop-shadow(0 0 3px rgba(139, 92, 246, 0.3))",
                            ],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                        />
                      </motion.div>
                      
                      {/* Publisher Logo - second in RTL, first in LTR */}
                      <div className={`flex items-center justify-center w-6 h-6 rounded-md bg-gray-100 overflow-hidden shrink-0 ${isRTL ? 'order-2' : 'order-1'}`}>
                        {(() => {
                          // Use project iconUrl with fallback to parent domain logo
                          const logoUrl = projectIconUrl || getParentLogoUrl(24, 24);
                          const parentDomain = getParentDomain();
                          
                          return logoUrl ? (
                            <img 
                              src={logoUrl} 
                              alt={projectIconUrl ? "Project logo" : `${parentDomain} logo`}
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                // Fallback to domain initial if logo fails to load
                                e.currentTarget.style.display = 'none';
                                const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                          ) : null;
                        })()}
                        <div 
                          className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-100 flex items-center justify-center text-gray-600 font-semibold text-xs"
                          style={{ display: (projectIconUrl || getParentLogoUrl(24, 24)) ? 'none' : 'flex' }}
                        >
                          {getParentDomain() ? getParentDomain().charAt(0).toUpperCase() : '?'}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Placeholder text - show thinking when streaming, normal placeholder otherwise */}
                  {!isFocused && !inputValue && (
                    <div className={`flex-1 min-w-0 ${isRTL ? 'order-1' : 'order-2'}`}>
                      <AnimatePresence mode="wait">
                        {isStreaming ? (
                          <motion.span
                            key="thinking"
                            className={`text-black-500 select-none pointer-events-none truncate block ${isRTL ? 'text-right' : 'text-left'}`}
                            style={{
                              zIndex: 0,
                              textAlign: isRTL ? 'right' : 'left',
                              direction: isRTL ? 'rtl' : 'ltr'
                            }}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.1 }}
                          >
                            <span className="flex items-center gap-1">
                              <span>Thinking</span>
                              <span className="animate-pulse">...</span>
                            </span>
                          </motion.span>
                        ) : showPlaceholder ? (
                          <motion.span
                            key={placeholderIndex}
                            className={`text-gray-500 select-none pointer-events-none truncate block ${isRTL ? 'text-right' : 'text-left'}`}
                            style={{
                              zIndex: 0,
                              textAlign: isRTL ? 'right' : 'left',
                              direction: isRTL ? 'rtl' : 'ltr'
                            }}
                            variants={placeholderContainerVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                          >
                            {placeholders[placeholderIndex]
                              .split("")
                              .map((char, i) => (
                                <motion.span
                                  key={i}
                                  variants={letterVariants}
                                  style={{ display: "inline-block" }}
                                >
                                  {char === " " ? "\u00A0" : char}
                                </motion.span>
                              ))}
                          </motion.span>
                        ) : null}
                      </AnimatePresence>
                    </div>
                  )}
                </div>
              </div>
            </div>
 
            {/* Send/Stop Button - Always at the end */}
            <div className="flex justify-end">
              {isStreaming ? (
                <button
                  className="flex items-center gap-1 bg-black hover:bg-gray-800 text-white p-3 rounded-full font-medium justify-center flex-shrink-0 transition-colors focus:outline-none focus:ring-0 smooth-appearance"
                  title="Stop generating"
                  type="button"
                  tabIndex={-1}
                  onClick={onStop}
                  style={{
                    outline: 'none',
                    WebkitAppearance: 'none',
                    WebkitTapHighlightColor: 'transparent',
                    boxShadow: 'none'
                  }}
                >
                  <Square size={14} />
                </button>
              ) : (
                <button
                  className={`flex items-center gap-1 p-3 rounded-full font-medium justify-center flex-shrink-0 transition-colors focus:outline-none focus:ring-0 smooth-appearance ${
                    disabled || !inputValue.trim() || isOverLimit
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                  title={isOverLimit ? "Message too long" : disabled ? "Input disabled" : "Send"}
                  type="button"
                  tabIndex={-1}
                  disabled={disabled || !inputValue.trim() || isOverLimit}
                  onClick={handleSend}
                  style={{
                    outline: 'none',
                    WebkitAppearance: 'none',
                    WebkitTapHighlightColor: 'transparent',
                    boxShadow: 'none'
                  }}
                >
                  <Send size={18} />
                </button>
              )}
            </div>
          </div>

          {/* Error message when over token limit */}
          {isOverLimit && (
            <div className="px-4 pb-2 flex justify-center">
              <div className="text-xs text-red-500">
                Message too long
              </div>
            </div>
          )}
        </div>
        </div>
      </motion.div>
    </div>
  );
};
 
export { AIChatInput }; 